import { useTranslation } from 'react-i18next'
import type { ReactElement } from 'react'
import { MapRow } from '@metro/station-details'
import type { StationExit } from './fetchStationContent'
import styles from './StationExitsSection.module.css'

interface Props {
  stationName: string
  /** Geographic position — the map tile and the external link need it. */
  coordinate: { readonly latitude: number; readonly longitude: number } | undefined
  /** Exits with nearby landmarks from the station-content database. */
  exits: readonly StationExit[]
}

/**
 * Exits and transport — Figma 8597:41732 (TASK-699). Left: the Apple
 * map tile the StationDetails panel already ships (`MapRow`, MapKit JS
 * behind /api/v2/mapkit/token). Right: numbered exits with their nearby landmarks —
 * editorial rows from the station-content database
 * (`station-content/<lng>/<naptanId>.json`, content spec 2026-08-05); stations without
 * exits data show the map alone.
 *
 * No coordinate → nothing renders (the map IS the section).
 */
export function StationExitsSection({
  stationName,
  coordinate,
  exits,
}: Props): ReactElement | null {
  const { t } = useTranslation('station-page')
  if (coordinate === undefined) return null

  return (
    <div className={styles.section}>
      <p className={styles.caption}>{t('exits_caption')}</p>
      <div className={styles.grid}>
        <div className={styles.mapColumn}>
          <div className={styles.mapFrame}>
            <MapRow
              stationName={stationName}
              lat={coordinate.latitude}
              lng={coordinate.longitude}
            />
          </div>
        </div>
        {exits.length > 0 && (
          <div className={styles.exits}>
            {exits.map((exit, index) => (
              <div key={exit.name} className={styles.exitRow}>
                <span className={styles.exitNumber}>{index + 1}</span>
                <span className={styles.exitText}>
                  <span className={styles.exitName}>{exit.name}</span>
                  {exit.nearby !== undefined && (
                    <span className={styles.exitNearby}>{exit.nearby}</span>
                  )}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
